import Router from 'next/router';
import { Action, Store } from 'redux';
import { differenceInDays } from 'date-fns';
import { truncate } from 'underscore.string';
import { showModal } from 'src/modules/shared/modal/actions';
import { fetchCurrentUser, fetchPermissions, fetchUnreadMessagesCount, fetchUnreadNotificationsCount } from 'src/modules/shared/context/actions';
import { fetchEmailPreferences } from 'src/api/emailNotifications';
import api from 'src/utils/api';
import { setLocalStorageItem, getLocalStorageItem, Key } from 'src/utils/localStorage';
import modalTypes from 'src/constants/modalTypes';
import contextActionTypes from 'src/modules/shared/context/actionTypes';
import { State, Community } from 'src/types';

const context = ({getState, dispatch}: Store) => (next: any) => async (action: Action) => {
  const result = next(action);
  const state = getState();
  switch (action.type) {
    case contextActionTypes.SIGN_UP_SUCCESS:
    case contextActionTypes.SOCIAL_SIGN_UP_SUCCESS:
      storeLastViewedPost(state);
      await fetchUserData(dispatch, state.context.currentCommunity);
      break;
    case contextActionTypes.LOGIN_SUCCESS:
    case contextActionTypes.SOCIAL_LOGIN_SUCCESS:
      marketingOptinPrompt(dispatch, getState);
      break;
    default:
      break;
  }

  return result;
};
export default context;

const storeLastViewedPost = (state: State) => {
  const isSinglePostPage = Router.route === '/community/singlePost';
  if (!isSinglePostPage || !state.pages.singlePost?.post) return;

  const { pages: { singlePost: { post } } } = state;
  const lastPost = {
    postId: post.id,
    snippet: truncate(post.body, 80),
    authorAvatarImage: post.author.avatarMedium,
    title: post.title,
    link: post.postUrl,
  };
  setLocalStorageItem(Key.lastViewedPost, JSON.stringify(lastPost));
};

// After a successful sign up/social sign up
const fetchUserData = async (dispatch: any, currentCommunity: Community) => {
  await dispatch(fetchCurrentUser());
  dispatch(fetchPermissions(currentCommunity?.communityId));
  dispatch(fetchUnreadMessagesCount());
  dispatch(fetchUnreadNotificationsCount());
};

const marketingOptinPrompt = async (dispatch: any, getState: any) => {
  const NOTIFICATION_ID = 55;
  const HALF_A_YEAR = 183;
  const DONT_SHOW_AFTER_JOINING = 5;
  const IGNORE_ROUTES = [
    '/unsubscribe',
    '/emailNotifications',
    '/user/editProfile',
  ];
  const today = new Date();

  // Don't show on ignored routes
  if (IGNORE_ROUTES.some(route => route === Router.pathname)) {
    window.location.reload();
    return;
  }

  // Don't show if less than 6 months since last prompt
  const lastPromptDate = getLocalStorageItem(Key.marketingOptinPromptDate);
  if (lastPromptDate && differenceInDays(today, new Date(lastPromptDate)) < HALF_A_YEAR) {
    window.location.reload();
    return;
  }

  // Don't show if user has opted in already
  await dispatch(fetchCurrentUser());
  const state = getState();
  const emailPreferences = await fetchEmailPreferences(api, undefined, state.context?.currentUser?.userId);
  const isMarketingEnabled = emailPreferences?.find(({notificationId}) => notificationId === NOTIFICATION_ID)?.enabled;
  if (isMarketingEnabled) {
    window.location.reload();
    return;
  }

  // Don't show if user has joined in 5 days
  const daysSinceJoining = differenceInDays(today, new Date(state.context.currentUser.dateCreated));
  if (daysSinceJoining < DONT_SHOW_AFTER_JOINING) {
    window.location.reload();
    return;
  }

  // Set localStorage and show modal
  setLocalStorageItem(Key.marketingOptinPromptDate, today.toISOString());
  dispatch(showModal({modalType: modalTypes.MARKETING_OPT_IN}));
};
